/**
 * Shared contract for the three intake methods (form, chat, PDFs) so the same
 * bodies render both as full pages (/projects/new[/chat|/pdf]) and embedded in
 * the calendar's New-project/request modal (§L parity, calendar intake).
 *
 * Page variant: route-based IntakeHeader switcher, session-persisted state (so
 * switching methods keeps your work), and server-side draft autosave.
 * Modal variant: an in-modal switcher owns the method; state is ephemeral
 * (fresh per open) and autosave is off — the modal is a quick, seeded path.
 */
import { useState, type Dispatch, type SetStateAction } from 'react';
import type { Project, ProjectCreateInput } from '@vari-crew/schemas';
import { usePersistedState } from '@/lib/intakeSession';

export type IntakeVariant = 'page' | 'modal';

/** Schedule a calendar slot seeds onto the resulting project (modal only). */
export interface IntakeSeed {
  requested_schedule?: ProjectCreateInput['requested_schedule'];
  requested_start_date?: ProjectCreateInput['requested_start_date'];
  requested_end_date?: ProjectCreateInput['requested_end_date'];
}

/** Files + a recap handed from the PDF method into the chat method. */
export interface PdfChatHandoff {
  summary: string;
  files: File[];
}

export interface IntakePanelProps {
  variant: IntakeVariant;
  /** Calendar slot schedule to seed onto the resulting project (modal). */
  seed?: IntakeSeed;
  /** Created project → page navigates to it; modal closes + refreshes the calendar. */
  onCreated: (project: Project) => void;
  /** Report unsaved content so the modal can guard an accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * State that persists across method-tab switches on the full-page intake (via the
 * in-memory session cache) but is ephemeral inside the calendar modal (fresh per
 * open). Page → usePersistedState(key); modal → plain useState. Both hooks are
 * always called (rules of hooks); only the selected tuple is returned. Modal keys
 * are namespaced ('cal.*') so an unused persisted cell never clobbers the page's.
 */
export function useIntakeState<T>(
  persist: boolean,
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  const persisted = usePersistedState<T>(key, initial);
  const local = useState<T>(initial);
  return persist ? persisted : local;
}

/**
 * Overlay a calendar slot's schedule onto a draft. The slot is what the user
 * drew on the calendar, so it wins for the "when" fields over anything the AI
 * extracted; everything else on the draft is left untouched.
 */
export function applySeed(
  draft: Partial<ProjectCreateInput> | null,
  seed: IntakeSeed | undefined,
): Partial<ProjectCreateInput> {
  const base = draft ?? {};
  if (!seed) return base;
  // The seed (a drawn calendar slot) is a DEFAULT, not an override: it fills the
  // "when" only when the draft has none of its own. Anything the PM actually
  // stated wins — typed in the form, said in chat, or AI-extracted — so we never
  // clobber an explicit schedule (e.g. chat "June 19, 7am–10pm" must beat a
  // rough 3-day slot drag). Treat the schedule as one unit so we don't, say,
  // graft the slot's end day onto an AI-extracted single day.
  const hasTiming = (base.requested_schedule?.length ?? 0) > 0 || !!base.requested_start_date;
  if (hasTiming) return base;
  return {
    ...base,
    ...(seed.requested_schedule ? { requested_schedule: seed.requested_schedule } : {}),
    ...(seed.requested_start_date !== undefined
      ? { requested_start_date: seed.requested_start_date }
      : {}),
    ...(seed.requested_end_date !== undefined
      ? { requested_end_date: seed.requested_end_date }
      : {}),
  };
}
